'use client';

import React, { useState } from 'react';

import AppLogo from '@/components/ui/AppLogo';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useForm } from 'react-hook-form';

type AuthTab = 'login' | 'signup';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: 'user' | 'contributor';
  agreeTerms: boolean;
}

const demoCredentials = [
{ role: 'Admin', email: 'admin@cineworld.cloud', password: 'Cine@Admin2026' },
{ role: 'Contributor', email: 'marcus@cineworld.cloud', password: 'Cine@Contrib2026' },
{ role: 'User', email: 'sofia@cineworld.cloud', password: 'Cine@User2026' }];


const backdropImages = [
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6d7ec3f-1772140693587.png", alt: 'Cinematic film projector light beam in dark theater' },
{ src: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6ea8eef-1784119722063.png", alt: 'Classic cinema projector with light beam' },
{ src: "https://images.unsplash.com/photo-1617620270439-66938ad0eb7e", alt: 'Empty cinema seats with red upholstery' }];


export default function AuthScreen() {
  const [activeTab, setActiveTab] = useState<AuthTab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [backdropIdx] = useState(0);

  const loginForm = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });
  const signupForm = useForm<SignupFormData>({ defaultValues: { role: 'user', agreeTerms: false } });

  const handleLogin = async (data: LoginFormData) => {
    setAuthError('');
    // Backend integration: POST /api/auth/login with { email, password }
    await new Promise((r) => setTimeout(r, 1200));
    const valid = demoCredentials.some(
      (c) => c.email === data.email && c.password === data.password
    );
    if (!valid) {
      setAuthError('Invalid credentials — use the demo accounts below to sign in');
      return;
    }
    window.location.href = '/';
  };

  const handleSignup = async (data: SignupFormData) => {
    // Backend integration: POST /api/auth/register with { name, email, password, role }
    await new Promise((r) => setTimeout(r, 1400));
    window.location.href = '/user-dashboard';
  };

  const autofillCredential = (cred: typeof demoCredentials[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    setActiveTab('login');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel — cinematic backdrop */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden">
        <AppImage
          src={backdropImages[backdropIdx].src}
          alt={backdropImages[backdropIdx].alt}
          fill
          priority
          className="object-cover"
          sizes="60vw" />
        
        <div className="absolute inset-0 bg-gradient-to-r from-background/20 via-background/10 to-background/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />

        {/* Overlay content */}
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <div className="max-w-md">
            <div className="flex items-center gap-2 mb-6">
              <span className="px-3 py-1 rounded-full text-xs font-700 badge-trending">Featured</span>
            </div>
            <h2 className="text-3xl font-800 text-foreground leading-tight mb-3">
              Dune: Part Two
            </h2>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="StarIcon" variant="solid" size={14} className="rating-star-filled" />
              <span className="text-sm font-700 text-accent">4.6</span>
              <span className="text-sm text-muted-foreground">· 14,820 reviews</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Join thousands of film enthusiasts on Cineworld. Discover, review, and discuss the movies that matter.
            </p>
          </div>
        </div>

        {/* Film strip decoration */}
        <div className="absolute top-0 left-0 bottom-0 w-8 flex flex-col justify-around opacity-20">
          {Array.from({ length: 20 }, (_, i) =>
          <div key={`filmstrip-${i}`} className="w-5 h-4 mx-auto bg-foreground/30 rounded-sm" />
          )}
        </div>
      </div>

      {/* Right panel — auth form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <AppLogo size={36} />
            <span className="font-sans text-2xl font-800 text-foreground">
              Cine<span className="text-gradient-red">world</span>
            </span>
          </div>

          {/* Tab switcher */}
          <div className="flex rounded-xl bg-card border border-border p-1 mb-6">
            {(['login', 'signup'] as AuthTab[]).map((tab) =>
            <button
              key={`auth-tab-${tab}`}
              onClick={() => {setActiveTab(tab);setAuthError('');}}
              className={`flex-1 py-2.5 text-sm font-600 rounded-lg transition-all duration-150 ${
              activeTab === tab ?
              'bg-primary text-white shadow-sm' :
              'text-muted-foreground hover:text-foreground'}`
              }>
              
                {tab === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            )}
          </div>

          {/* Error */}
          {authError &&
          <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/10 border border-primary/25 mb-4">
              <Icon name="ExclamationCircleIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-primary">{authError}</p>
            </div>
          }

          {/* LOGIN FORM */}
          {activeTab === 'login' &&
          <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4 fade-in">
              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Email Address</label>
                <input
                {...loginForm.register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' }
                })}
                type="email"
                placeholder="you@cineworld.cloud"
                className="input-dark w-full px-4 py-3 text-sm"
                autoComplete="email" />
              
                {loginForm.formState.errors.email &&
              <p className="text-xs text-primary mt-1">{loginForm.formState.errors.email.message}</p>
              }
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <input
                  {...loginForm.register('password', { required: 'Password is required' })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  className="input-dark w-full px-4 py-3 pr-11 text-sm"
                  autoComplete="current-password" />
                
                  <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {loginForm.formState.errors.password &&
              <p className="text-xs text-primary mt-1">{loginForm.formState.errors.password.message}</p>
              }
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                  {...loginForm.register('rememberMe')}
                  type="checkbox"
                  className="rounded border-border" />
                
                  <span className="text-sm text-muted-foreground">Remember me</span>
                </label>
                <button type="button" className="text-sm text-accent hover:underline">
                  Forgot password?
                </button>
              </div>

              <button
              type="submit"
              disabled={loginForm.formState.isSubmitting}
              className="btn-primary w-full py-3 rounded-xl text-sm font-700 flex items-center justify-center gap-2">
              
                {loginForm.formState.isSubmitting ?
              <>
                    <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                    Signing in...
                  </> :
              'Sign In to Cineworld'}
              </button>

              <div className="relative flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">or continue with</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
              { label: 'Google', icon: 'GlobeAltIcon' },
              { label: 'GitHub', icon: 'CodeBracketIcon' }].
              map((provider) =>
              <button
                key={`oauth-${provider.label}`}
                type="button"
                className="btn-ghost flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-500">
                
                    <Icon name={provider.icon as Parameters<typeof Icon>[0]['name']} size={16} />
                    {provider.label}
                  </button>
              )}
              </div>
            </form>
          }

          {/* SIGNUP FORM */}
          {activeTab === 'signup' &&
          <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4 fade-in">
              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Full Name</label>
                <input
                {...signupForm.register('name', { required: 'Name is required', minLength: { value: 2, message: 'Minimum 2 characters' } })}
                type="text"
                placeholder="Your full name"
                className="input-dark w-full px-4 py-3 text-sm" />
              
                {signupForm.formState.errors.name &&
              <p className="text-xs text-primary mt-1">{signupForm.formState.errors.name.message}</p>
              }
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Email Address</label>
                <input
                {...signupForm.register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' }
                })}
                type="email"
                placeholder="you@example.com"
                className="input-dark w-full px-4 py-3 text-sm" />
              
                {signupForm.formState.errors.email &&
              <p className="text-xs text-primary mt-1">{signupForm.formState.errors.email.message}</p>
              }
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <input
                  {...signupForm.register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Minimum 8 characters' }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  className="input-dark w-full px-4 py-3 pr-11 text-sm" />
                
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Toggle password visibility">
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {signupForm.formState.errors.password &&
              <p className="text-xs text-primary mt-1">{signupForm.formState.errors.password.message}</p>
              }
              </div>

              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Confirm Password</label>
                <div className="relative">
                  <input
                  {...signupForm.register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (val) => val === signupForm.watch('password') || 'Passwords do not match'
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Repeat your password"
                  className="input-dark w-full px-4 py-3 pr-11 text-sm" />
                
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Toggle confirm password visibility">
                    <Icon name={showConfirmPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword &&
              <p className="text-xs text-primary mt-1">{signupForm.formState.errors.confirmPassword.message}</p>
              }
              </div>

              {/* Role selector */}
              <div>
                <label className="block text-sm font-500 text-foreground mb-1.5">Account Type</label>
                <p className="text-xs text-muted-foreground mb-2">Choose how you want to participate in Cineworld</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                { value: 'user', label: 'Movie Fan', desc: 'Browse, review & track films', icon: 'UserIcon' },
                { value: 'contributor', label: 'Contributor', desc: 'Also submit new movies', icon: 'PlusCircleIcon' }].
                map((role) => {
                  const selected = signupForm.watch('role') === role.value;
                  return (
                    <label
                      key={`role-${role.value}`}
                      className={`flex flex-col gap-1 p-3 rounded-xl border cursor-pointer transition-all duration-150 ${
                      selected ? 'border-primary bg-primary/10' : 'border-border bg-card hover:border-border/80'}`
                      }>
                      
                        <input
                        {...signupForm.register('role')}
                        type="radio"
                        value={role.value}
                        className="sr-only" />
                      
                        <div className="flex items-center gap-2">
                          <Icon name={role.icon as Parameters<typeof Icon>[0]['name']} size={14} className={selected ? 'text-primary' : 'text-muted-foreground'} />
                          <span className={`text-sm font-600 ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>{role.label}</span>
                        </div>
                        <span className="text-xs text-muted-foreground leading-snug">{role.desc}</span>
                      </label>);

                })}
                </div>
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                {...signupForm.register('agreeTerms', { required: 'You must agree to the terms' })}
                type="checkbox"
                className="rounded border-border mt-0.5" />
              
                <span className="text-xs text-muted-foreground leading-relaxed">
                  I agree to the{' '}
                  <a href="#" className="text-accent hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-accent hover:underline">Privacy Policy</a>
                </span>
              </label>
              {signupForm.formState.errors.agreeTerms &&
            <p className="text-xs text-primary">{signupForm.formState.errors.agreeTerms.message}</p>
            }

              <button
              type="submit"
              disabled={signupForm.formState.isSubmitting}
              className="btn-primary w-full py-3 rounded-xl text-sm font-700 flex items-center justify-center gap-2">
              
                {signupForm.formState.isSubmitting ?
              <>
                    <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                    Creating account...
                  </> :
              'Create My Account'}
              </button>
            </form>
          }

          {/* Demo credentials */}
          <div className="mt-6 p-4 rounded-xl bg-card border border-border">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="KeyIcon" size={14} className="text-gold" />
              <span className="text-xs font-700 text-gold uppercase tracking-wider">Demo Accounts</span>
            </div>
            <div className="space-y-2">
              {demoCredentials.map((cred) =>
              <div key={`demo-${cred.role}`} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/40 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-xs font-600 px-2 py-0.5 rounded-full flex-shrink-0 ${
                  cred.role === 'Admin' ? 'bg-primary/15 text-primary border border-primary/25' :
                  cred.role === 'Contributor' ? 'bg-purple-500/15 text-purple-400 border border-purple-500/25' : 'bg-muted text-muted-foreground border border-border'}`
                  }>{cred.role}</span>
                    <span className="text-xs text-muted-foreground truncate font-mono">{cred.email}</span>
                  </div>
                  <button
                  type="button"
                  onClick={() => autofillCredential(cred)}
                  className="flex-shrink-0 text-xs text-accent hover:underline font-600 transition-colors">
                  
                    Use
                  </button>
                </div>
              )}
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-4">
            {activeTab === 'login' ?
            <>Don&apos;t have an account?{' '}
                <button onClick={() => setActiveTab('signup')} className="text-accent hover:underline font-600">Sign up free</button>
              </> :

            <>Already have an account?{' '}
                <button onClick={() => setActiveTab('login')} className="text-accent hover:underline font-600">Sign in</button>
              </>
            }
          </p>
        </div>
      </div>
    </div>);

}